import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Hash,
  DollarSign,
  User,
  ArrowRight,
  CheckCircle,
  ShieldCheck,
  Calculator,
  Percent,
  Info,
  ArrowUpRight,
  Wallet,
} from 'lucide-react';
import { AgentHeader } from '../../components/layout/AgentHeader';
import { AgentSidebar } from '../../components/layout/AgentSidebar';
import { BottomNav } from '../../components/layout/BottomNav';
import { cashTransactionsApi } from '../../api/cash-transactions.api';
import { CashOutValidateResponse } from '../../types/cash-transaction.types';
import { useFeeEstimate } from '../../hooks/useFeeEstimate';

// ─── Pasos del flujo ──────────────────────────────────────────────────────────
type Step = 'enter-code' | 'confirm' | 'success';

// ─── Schemas ──────────────────────────────────────────────────────────────────
const codeSchema = z.object({
  code: z
    .string()
    .length(6, 'El código debe tener exactamente 6 dígitos')
    .regex(/^\d{6}$/, 'El código solo debe contener números'),
});

type CodeFormData = z.infer<typeof codeSchema>;

export default function CashOutPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('enter-code');
  const [validated, setValidated] = useState<CashOutValidateResponse | null>(null);
  const [completedTx, setCompletedTx] = useState<any>(null);
  const [previewAmount, setPreviewAmount] = useState<number | null>(null);

  // Estimación de fee en tiempo real (paso 1)
  const { estimate, isLoading: estimateLoading } = useFeeEstimate('cash-out', previewAmount);

  // ─── Formulario Paso 1: código OTP ──────────────────────────────────────
  const codeForm = useForm<CodeFormData>({
    resolver: zodResolver(codeSchema),
  });

  // ─── Mutation: validar código ─────────────────────────────────────────────
  const validateMutation = useMutation({
    mutationFn: (code: string) => cashTransactionsApi.validateCashOutCode(code),
    onSuccess: (data) => {
      setValidated(data);
      setStep('confirm');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Código inválido o expirado');
    },
  });

  // ─── Mutation: confirmar cash-out ─────────────────────────────────────────
  const confirmMutation = useMutation({
    mutationFn: () =>
      cashTransactionsApi.confirmCashOut({
        code: codeForm.getValues('code'),
        amount: validated!.amount,
        agentPin: '',
      }),
    onSuccess: (data) => {
      setCompletedTx(data);
      setStep('success');
      queryClient.invalidateQueries({ queryKey: ['agentProfile'] });
      queryClient.invalidateQueries({ queryKey: ['recentTransactions'] });
      queryClient.invalidateQueries({ queryKey: ['cash-transactions'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al confirmar el retiro');
    },
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const onValidate = (data: CodeFormData) => validateMutation.mutate(data.code);
  const onConfirm = () => confirmMutation.mutate();

  const handleReset = () => {
    setStep('enter-code');
    setValidated(null);
    setCompletedTx(null);
    codeForm.reset();
  };

  const handleFinish = () => {
    handleReset();
    navigate('/');
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <AgentHeader />

      <div className="flex">
        <AgentSidebar />

        <main className="flex-1 pb-20 lg:pb-6">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
            <div className="max-w-2xl mx-auto px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ArrowRight className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Retiro de Efectivo</h1>
                  <p className="text-sm text-gray-600">Cash-Out</p>
                </div>
              </div>
            </div>
          </header>

          {/* Steps indicator */}
          <div className="max-w-2xl mx-auto px-4 pt-6">
            <div className="flex items-center gap-2 mb-6">
              {(['enter-code', 'confirm', 'success'] as Step[]).map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                      step === s
                        ? 'bg-red-600 text-white'
                        : ['confirm', 'success'].includes(step) && i === 0
                        ? 'bg-red-200 text-red-800'
                        : step === 'success' && i === 1
                        ? 'bg-red-200 text-red-800'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {i + 1}
                  </div>
                  {i < 2 && <div className="h-0.5 w-8 bg-gray-200" />}
                </div>
              ))}
              <div className="ml-2 text-sm text-gray-500">
                {step === 'enter-code' && 'Ingresar código del cliente'}
                {step === 'confirm' && 'Confirmar operación'}
                {step === 'success' && '¡Completado!'}
              </div>
            </div>
          </div>

          <div className="max-w-2xl mx-auto px-4 pb-6 space-y-6">

            {/* ── Paso 1: Código OTP ─────────────────────────────────────── */}
            {step === 'enter-code' && (
              <form onSubmit={codeForm.handleSubmit(onValidate)} className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <Hash className="w-5 h-5 text-red-600" />
                    Código del Cliente
                  </h2>
                  <p className="text-sm text-gray-500 mb-5">
                    Pide al cliente que genere un código de retiro en su app y muéstratelo.
                    Ingresa aquí ese código de 6 dígitos.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Código OTP (6 dígitos)
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      {...codeForm.register('code')}
                      placeholder="7 3 9 0 2 1"
                      className="w-full px-4 py-4 text-3xl font-bold tracking-widest text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    {codeForm.formState.errors.code && (
                      <p className="text-sm text-red-600 mt-1">
                        {codeForm.formState.errors.code.message}
                      </p>
                    )}
                  </div>

                  {/* Estimador de fee en tiempo real */}
                  <div className="mt-5 border-t border-gray-100 pt-5">
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                      <Calculator className="w-4 h-4 text-red-600" />
                      Calculadora de fee en tiempo real
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <ArrowUpRight className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="number"
                          inputMode="numeric"
                          min={500}
                          placeholder="Monto del retiro, ej. 25000"
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            setPreviewAmount(v > 0 ? v : null);
                          }}
                          className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-500 whitespace-nowrap">XAF</span>
                    </div>

                    {/* Spinner mientras espera debounce o fetch */}
                    {estimateLoading && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                        <span className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin inline-block" />
                        Calculando fee...
                      </div>
                    )}

                    {/* Panel de resultado */}
                    {estimate && !estimateLoading && (
                      <div className="mt-3 rounded-xl border border-red-200 overflow-hidden text-sm">
                        {/* Header */}
                        <div className="bg-red-600 px-4 py-2.5 flex items-center justify-between">
                          <span className="text-white font-semibold flex items-center gap-1.5">
                            <Percent className="w-4 h-4" />
                            Tasa: {estimate.commissionRate}%
                          </span>
                          <span className="text-red-100 text-xs uppercase tracking-wide">
                            {estimate.rateSource === 'global' ? 'Tarifa global' : 'Tarifa personalizada'}
                          </span>
                        </div>

                        {/* Filas */}
                        <div className="bg-red-50 divide-y divide-red-100">
                          <div className="flex justify-between items-center px-4 py-2">
                            <span className="text-gray-500 flex items-center gap-1.5">
                              <DollarSign className="w-3.5 h-3.5" />
                              Efectivo a entregar al cliente
                            </span>
                            <span className="font-semibold text-gray-900">
                              {estimate.amount.toLocaleString()} {estimate.currency}
                            </span>
                          </div>

                          <div className="flex justify-between items-center px-4 py-2">
                            <span className="text-gray-500 flex items-center gap-1.5">
                              <Percent className="w-3.5 h-3.5" />
                              Comisión cobrada al cliente ({estimate.commissionRate}%)
                            </span>
                            <span className="font-semibold text-orange-600">
                              + {estimate.fee.toLocaleString()} {estimate.currency}
                            </span>
                          </div>

                          <div className="flex justify-between items-center px-4 py-2.5 bg-red-100">
                            <span className="text-gray-700 font-semibold flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5" />
                              Total debitado al cliente
                            </span>
                            <span className="font-bold text-red-800 text-base">
                              {(estimate.customerPays ?? estimate.amount + estimate.fee).toLocaleString()} {estimate.currency}
                            </span>
                          </div>

                          <div className="flex justify-between items-center px-4 py-2">
                            <span className="text-gray-600 font-medium flex items-center gap-1.5">
                              <Wallet className="w-3.5 h-3.5" />
                              Crédito a tu float
                            </span>
                            <span className="font-bold text-green-700">
                              + {(estimate.agentCredited ?? estimate.amount).toLocaleString()} {estimate.currency}
                            </span>
                          </div>
                        </div>

                        {/* Nota informativa */}
                        {estimate.note && (
                          <div className="bg-white px-4 py-2.5 flex items-start gap-2 border-t border-red-100">
                            <Info className="w-3.5 h-3.5 text-red-600 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-gray-500 leading-relaxed">{estimate.note}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={validateMutation.isPending}
                    className="mt-5 w-full py-4 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {validateMutation.isPending ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Validando...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-5 h-5" />
                        Validar Código
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* ── Paso 2: Confirmar ─────────────────────────────────────── */}
            {step === 'confirm' && validated && (
              <div className="space-y-6">
                {/* Info del cliente */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-red-600" />
                    Datos del Cliente
                  </h2>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-500">Nombre</span>
                      <span className="font-semibold">{validated.customer.name}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-500">Teléfono</span>
                      <span className="font-semibold">{validated.customer.phone}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500">Wallet</span>
                      <span className="font-semibold">{validated.customer.walletNumber}</span>
                    </div>
                  </div>
                </div>

                {/* Monto */}
                <div className="bg-red-50 rounded-xl border border-red-200 p-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-red-600" />
                    Resumen de la Operación
                  </h2>
                  <div className="space-y-0 text-sm divide-y divide-red-100">
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500">Código OTP</span>
                      <span className="font-bold tracking-widest">{validated.code}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500">Monto a retirar</span>
                      <span className="font-semibold text-gray-900">{validated.amount.toLocaleString()} {validated.currency}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500">Fee agente ({validated.commissionRate}%)</span>
                      <span className="font-semibold text-orange-600">+ {validated.fee.toLocaleString()} {validated.currency}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-700 font-medium">Total debitado al cliente</span>
                      <span className="font-bold text-red-700 text-base">{validated.customerPays.toLocaleString()} {validated.currency}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-700 font-medium">Efectivo a entregar</span>
                      <span className="font-bold text-gray-900 text-base">{validated.amount.toLocaleString()} {validated.currency}</span>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    💵 Entrega este monto en efectivo al cliente tras confirmar.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={confirmMutation.isPending}
                    className="flex-2 flex-grow py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {confirmMutation.isPending ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Confirmar Retiro
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* ── Paso 3: Éxito ──────────────────────────────────────────── */}
            {step === 'success' && completedTx && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">¡Retiro Exitoso!</h3>
                <p className="text-gray-500 mb-6 text-sm">
                  El saldo ha sido debitado de la wallet del cliente. Entrega el efectivo.
                </p>

                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm text-left divide-y divide-gray-200">
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Referencia</span>
                    <span className="font-mono font-semibold text-red-700">{completedTx.reference}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Cliente</span>
                    <span className="font-semibold">{completedTx.customerName}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Efectivo entregado</span>
                    <span className="font-bold text-red-700">{completedTx.amount.toLocaleString()} {completedTx.currency}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Fee agente ({completedTx.commissionRate}%)</span>
                    <span className="font-semibold text-orange-600">{completedTx.fee.toLocaleString()} {completedTx.currency}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Total debitado al cliente</span>
                    <span className="font-semibold text-red-700">{(completedTx.customerPays ?? completedTx.amount + completedTx.fee).toLocaleString()} {completedTx.currency}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Nuevo saldo wallet cliente</span>
                    <span className="font-semibold">{completedTx.newWalletBalance.toLocaleString()} {completedTx.currency}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleReset}
                    className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Nuevo Retiro
                  </button>
                  <button
                    onClick={handleFinish}
                    className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
                  >
                    Ir al Inicio
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <BottomNav />
    </div>
  );
}
